import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Button, Space, Divider } from 'antd';
import { WorkOrderLineItem, WorkOrderLinePart } from '../../types';

const { TextArea } = Input;
const { Option } = Select;

interface LineItemDrawerProps {
  open: boolean;
  item: WorkOrderLineItem | null;
  isNew: boolean;
  isLocked: boolean;
  workOrderId: number;
  onSave: (values: Partial<WorkOrderLineItem>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onClose: () => void;
}

interface FormValues {
  concern: string;
  cause: string;
  correction: string;
  labor_hours: number;
  labor_rate: number;
  shop_status: string;
  notes: string;
}

const inputStyle = { fontSize: 16 };

/** Bottom drawer with line item edit form for mobile. */
export const LineItemDrawer: React.FC<LineItemDrawerProps> = ({
  open,
  item,
  isNew,
  isLocked,
  workOrderId,
  onSave,
  onDelete,
  onClose,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Reset form when item changes
  useEffect(() => {
    if (open) {
      setShowDeleteConfirm(false);
      if (item) {
        form.setFieldsValue({
          concern: item.concern || '',
          cause: item.cause || '',
          correction: item.correction || '',
          labor_hours: item.labor_hours || 0,
          labor_rate: item.labor_rate || 0,
          shop_status: item.shop_status || 'new',
          notes: item.notes || '',
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, item, form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await onSave({
        ...(item ? { id: item.id } : {}),
        work_order_id: workOrderId,
        concern: values.concern,
        cause: values.cause,
        correction: values.correction,
        labor_hours: values.labor_hours,
        labor_rate: values.labor_rate,
        shop_status: values.shop_status as WorkOrderLineItem['shop_status'],
        notes: values.notes,
      });
      onClose();
    } catch {
      // validation error -- form shows inline messages
    } finally {
      setSaving(false);
    }
  }, [form, item, workOrderId, onSave, onClose]);

  const handleDelete = useCallback(async () => {
    if (!item?.id) return;
    setDeleting(true);
    try {
      await onDelete(item.id);
      onClose();
    } finally {
      setDeleting(false);
    }
  }, [item, onDelete, onClose]);

  const title = isNew ? 'Add Line Item' : 'Edit Line Item';

  const partsTotal = item?.parts?.reduce(
    (sum: number, p: WorkOrderLinePart) => sum + p.quantity * p.unit_price,
    0,
  ) || 0;
  const laborTotal = (item?.labor_hours || 0) * (item?.labor_rate || 0);

  return (
    <Drawer
      title={<span style={{ fontSize: 20, fontWeight: 600 }}>{title}</span>}
      placement="bottom"
      height="70vh"
      open={open}
      onClose={onClose}
      destroyOnClose={false}
      styles={{
        wrapper: { borderRadius: '16px 16px 0 0' },
        body: {
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          overflowY: 'auto',
        },
      }}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          concern: '',
          cause: '',
          correction: '',
          labor_hours: 0,
          labor_rate: 0,
          shop_status: 'new',
          notes: '',
        }}
      >
        <Form.Item
          name="concern"
          label="Concern (what customer reported)"
          rules={[{ required: true, message: 'Concern is required' }]}
        >
          <TextArea
            rows={2}
            style={inputStyle}
            placeholder="What did the customer report?"
            disabled={isLocked}
          />
        </Form.Item>

        <Form.Item name="cause" label="Cause (what we found)">
          <TextArea
            rows={2}
            style={inputStyle}
            placeholder="What was the root cause?"
            disabled={isLocked}
          />
        </Form.Item>

        <Form.Item name="correction" label="Correction (what we did)">
          <TextArea
            rows={2}
            style={inputStyle}
            placeholder="What repair was performed?"
            disabled={isLocked}
          />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="labor_hours" label="Labor Hours">
            <InputNumber
              min={0}
              step={0.5}
              style={{ ...inputStyle, width: '100%' }}
              disabled={isLocked}
            />
          </Form.Item>
          <Form.Item name="labor_rate" label="Labor Rate ($/hr)">
            <InputNumber
              min={0}
              step={5}
              prefix="$"
              style={{ ...inputStyle, width: '100%' }}
              disabled={isLocked}
            />
          </Form.Item>
        </div>

        <Form.Item name="shop_status" label="Shop Status">
          <Select style={inputStyle} disabled={isLocked}>
            <Option value="new">New</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="waiting_parts">Waiting Parts</Option>
            <Option value="need_approval">Needs Approval</Option>
            <Option value="completed">Completed</Option>
          </Select>
        </Form.Item>

        <Form.Item name="notes" label="Internal Notes">
          <TextArea
            rows={2}
            style={inputStyle}
            placeholder="Notes for the shop (not visible to customer)"
            disabled={isLocked}
          />
        </Form.Item>

        {!isNew && item && (
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
            <div>Labor: ${laborTotal.toFixed(2)}</div>
            {partsTotal > 0 && <div>Parts: ${partsTotal.toFixed(2)}</div>}
            <div style={{ fontWeight: 600, color: '#111827', marginTop: 4 }}>
              Line Total: ${((item.line_total != null ? item.line_total : laborTotal + partsTotal)).toFixed(2)}
            </div>
          </div>
        )}
      </Form>

      <Divider style={{ margin: '12px 0' }} />

      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Button
          type="primary"
          block
          loading={saving}
          onClick={handleSave}
          disabled={isLocked}
          style={{ height: 48, fontSize: 16 }}
        >
          Save Item
        </Button>
        <Button
          block
          onClick={onClose}
          style={{ height: 48, fontSize: 16 }}
        >
          Cancel
        </Button>

        {!isNew && item?.id && !isLocked && (
          <>
            {!showDeleteConfirm ? (
              <Button
                ghost
                danger
                block
                onClick={() => setShowDeleteConfirm(true)}
                style={{ height: 48, fontSize: 16, marginTop: 8 }}
              >
                Delete Item
              </Button>
            ) : (
              <div style={{
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: 16,
                marginTop: 8,
                background: '#fef2f2',
              }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#991b1b', marginBottom: 12 }}>
                  Delete this item? This cannot be undone.
                </div>
                <Space style={{ width: '100%' }}>
                  <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                  <Button
                    type="primary"
                    danger
                    loading={deleting}
                    onClick={handleDelete}
                  >
                    Delete Item
                  </Button>
                </Space>
              </div>
            )}
          </>
        )}
      </Space>
    </Drawer>
  );
};
